import { Injectable } from '@angular/core';
import { ParamMap } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import { environment } from '../../../environments/environment';
import { SearchSuggestionPayload } from './payload/search-suggestion-payload';
import { SearchSuggestionModel } from 'app/model/search-suggestion.model';
import { AppConfigService } from 'app/services/app-config/app-config.service';
import * as moment from 'vendors/moment-locale-th';
import { UtilitiesService } from '../utilities/utilities.service';
import { ActHttpService, HeaderTypes, ContentTypes } from '../act-http/act-http.service';
import { BudgetLimitModel } from '../../model/user.model';
import { SearchFormModel, DestinationModel, GuestModel } from 'app/model/search-form-model';
import { Language, SuggestionCategory } from 'app/model/constant';

export enum PurposeType {
  work,
  leisure
}

@Injectable()
export class SearchService {

  readonly defaultCheckInDateFromNow: number = 0;
  readonly defaultCheckOutDateFromNow: number = 1;
  readonly defaultDiffDate: number = 1;
  readonly defaultGuest: GuestModel[] = [{ "adult": 2, "children": [] }];
  private formatDate: string;

  constructor(
    private actHttp: ActHttpService,
    private appConfigService: AppConfigService,
    private utilitiesService: UtilitiesService
  ) {
    this.formatDate = this.appConfigService.getDateUrlFormat();
  }

  getSearchSuggestion(req): Observable<SearchSuggestionModel[]> {
    const options = this.actHttp.getRequestOptions(HeaderTypes.ACT_TOKEN, ContentTypes.JSON);
    const payload = new SearchSuggestionPayload();
    payload.str = req.str;
    payload.lang = req.lang;
    payload.lat = req.lat;
    payload.lon = req.lon;
    payload.companyId = req.companyId;
    payload.objective = req.objective;
    return this.actHttp
      .post(environment.endpoint.getSearchSuggestion, payload, options)
      .map(response => {
        const json = response.json();
        return (json.d.data as SearchSuggestionModel[]);
      });
  }

  getSearchFormModelFromQueryParams(queryParms: ParamMap): SearchFormModel {
    let destination = queryParms.get(`destination`),
      purpose = queryParms.get(`purpose`),
      guest = queryParms.get(`guests`),
      breakfast = queryParms.get(`breakfast`),
      overBudget = queryParms.get(`overBudget`),
      campaignId = queryParms.get(`campaignId`),
      searchModel = new SearchFormModel();

    if (!(destination && purpose && (purpose === `work` || purpose === `leisure`))) {
      throw new Error(`destination and purpose are required`);
    } else {
      searchModel.purpose = purpose;
      searchModel.destination = <DestinationModel>JSON.parse(this.utilitiesService.base64Decoder(destination));
      if (!(searchModel.destination.id && searchModel.destination.title) && searchModel.destination.suggestCat != SuggestionCategory.NEAR_BY) {
        throw new Error(`destination and purpose are required`);
      }
    }

    let checkIn = queryParms.get(`checkIn`);
    if (this.isInvalidDate(checkIn) || this.isPastFrom(checkIn)) {
      searchModel.checkInDate = moment().add(this.defaultCheckInDateFromNow, 'day').format(this.formatDate);
      searchModel.checkOutDate = moment().add(this.defaultCheckOutDateFromNow, 'day').format(this.formatDate);
    } else {
      searchModel.checkInDate = checkIn;
      let checkOut = queryParms.get(`checkOut`);
      searchModel.checkOutDate = this.isInvalidDate(checkOut) || this.isPastFrom(checkOut, checkIn) || checkIn === checkOut ? moment(checkIn, this.formatDate).add(this.defaultDiffDate, 'day').format(this.formatDate) : checkOut;
    }

    if (!guest) {
      searchModel.guests = this.defaultGuest;
    } else {
      searchModel.guests = <GuestModel[]>JSON.parse(this.utilitiesService.base64Decoder(guest));
      if (searchModel.guests.length <= 0) {
        searchModel.guests = this.defaultGuest;
      } else {
        for (let index in searchModel.guests) {
          let guestItem = searchModel.guests[index];
          if (!(guestItem.hasOwnProperty(`children`) && guestItem.hasOwnProperty(`adult`) && guestItem.children instanceof Array && !isNaN(guestItem.adult) && guestItem.adult > 0)) {
            searchModel.guests = this.defaultGuest;
            break;
          }
        }
      }
    }

    searchModel.breakfast = breakfast == 'true';
    searchModel.overBudget = overBudget ? overBudget == 'true' : true;
    if (campaignId) {
      searchModel.campaignId = Number(campaignId);
    }

    return searchModel;
  }

  isInvalidDate(dateString: string): boolean {
    return !dateString ||
      moment(dateString, this.formatDate).format(this.formatDate) != dateString ||
      !moment(dateString, this.formatDate).isValid()
  }

  isPastFrom(dateString: string, checkDate?: string): boolean {
    let fromDate = checkDate ? moment(checkDate, this.formatDate) : moment(moment().format(this.formatDate), this.formatDate);
    return moment(dateString, this.formatDate).isBefore(fromDate);
  }

  calculateMaxMinFilterPrice(min, max, budgetLimit: BudgetLimitModel, overBudget: boolean) {
    if (!budgetLimit.isLimitBudget || overBudget) {
      const finalMax = budgetLimit.budget && budgetLimit.budget > max ? budgetLimit.budget : max;
      return { min: min, max: finalMax };
    }
    const budget = budgetLimit.budget;

    switch (true) {
      case budget >= max:
        return { min: min, max: max };
      case budget < max && budget > min:
        return { min: min, max: budget };
      case budget < max && budget <= min:
        return { min: 0, max: 0 };
      default:
        return { min: min, max: max };
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/services/search/search.service.ts